import { defineStore } from "pinia";
import { ref } from "vue";
import { api } from "@lr-notitia/teamly/client";
import type { Note } from "@lr-notitia/teamly/schemas";
import { useDomainStore } from "./domain";
import { UserRole } from "@lr-notitia/notitia-designer/schemas";

let domainStore: ReturnType<typeof useDomainStore>;
function init() {
	domainStore = useDomainStore();
}

export const teamlySocketUrl = `wss://team.notitia.world`;
export const teamlyHttpUrl = `https://team.notitia.world`;

export const useTeamlyStore = defineStore("TeamlyStore", () => {
	const notes = ref<{ [namespace: string]: Note[] }>({});

	function getNotes(namespace: string): Promise<Note[]> {
		return new Promise((resolve, reject) => {
			api({ host: teamlyHttpUrl, basePath: "/api/v1.0" })
				.notes(namespace)
				.get()
				.then((d) => {
					if (!d) d = [];
					notes.value[namespace] = d;
					resolve(d);
				})
				.catch(() => reject());
		});
	}
	function addNote(
		namespace: string,
		userId: string,
		textColor: string,
		type: string,
		position?: { x: number; y: number }
	): Promise<Note> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api({ host: teamlyHttpUrl, basePath: "/api/v1.0" })
				.notes(namespace)
				.post("Note", userId, textColor, type, position)
				.then((d) => {
					if (!notes.value[namespace].some((n) => n.id === d.id)) {
						notes.value[namespace].push(d);
					}
					resolve(d);
				})
				.catch(() => reject());
		});
	}
	function updateNote(namespace: string, note: Note): Promise<Note> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api({ host: teamlyHttpUrl, basePath: "/api/v1.0" })
				.notes(namespace)
				.patch(note)
				.then((d) => {
					notes.value[namespace] = notes.value[namespace].map((n) => (n.id == d.id ? d : n));
					resolve(d);
				})
				.catch(() => reject());
		});
	}
	function deleteNote(namespace: string, noteId: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api({ host: teamlyHttpUrl, basePath: "/api/v1.0" })
				.notes(namespace)
				.del(noteId)
				.then((d) => {
					notes.value[namespace] = notes.value[namespace].filter((n) => n.id != noteId);
					resolve();
				})
				.catch(() => reject());
		});
	}

	return {
		notes,
		getNotes,
		addNote,
		updateNote,
		deleteNote,
	};
});
